/* eslint-disable no-tabs */
import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { withRouter } from 'react-router-dom';
import { Grid, InputLabel, TextField } from '@mui/material';

import MultiSelect from '../../common/multi-select';
import { RunStatusNames, isOnMobile } from '../../../lib/utils';
import StartedAndClosedDisplay from '../../common/started-closed-display';
import RunMap from './run-map';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignContent: 'space-around',
  },
  textField: {
    width: '50%',
    paddingBottom: '10px',
  },
  scrollable: {
    maxHeight: '460px',
    overflowY: 'auto',
    marginRight: '12px',
    maxWidth: '370px',
  },
  runNameLabel: {
    paddingTop: '3%',
  },
};

function BaseEditRunContent({
  currentRun,
  handleChangeRun,
  selectedDate,
  setSelectedDate,
  startTime,
  setStartTime,
  zoneOptions,
  handleChangeRoute,
  warehouseOptions,
  handleChangeWarehouse,
  handleQuantityChange,
  currentTarget,
  assignees,
  assigneeOptions,
  handleChangeWrangler,
  statusOptions,
  handleChangeStatus,
  selectedMarket,
  tz,
  getVehiclesAndLocations,
  isDynamic,
}) {
  return (
    <Grid style={isOnMobile() ? {} : styles.flexRow}>
      <Grid container style={isOnMobile() ? {} : styles.scrollable}>
        <Grid item xs={10} style={styles.runNameLabel}>
          <InputLabel>Name</InputLabel>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="text"
            name="name"
            variant="standard"
            sx={{ mt: -0.5 }}
            value={currentRun.name || ''}
            onChange={handleChangeRun}
            disabled={isDynamic}
          />
        </Grid>
        <Grid item container xs={12} md={12} sx={{ mt: 1 }}>
          <Grid item xs={6} style={{ paddingRight: '5%' }}>
            <InputLabel>Start date</InputLabel>
            <TextField
              fullWidth
              id="date"
              type="date"
              name="selectedDate"
              placeholder="mmm-dd-yy"
              format="YYYY-MM-DD"
              variant="standard"
              value={selectedDate}
              InputLabelProps={{
                shrink: true,
                min: dayjs().format('YYYY-MM-DD'),
              }}
              onChange={(e) => setSelectedDate(e.target.value)}
              disabled={isDynamic}
            />
          </Grid>
          <Grid item xs={5} style={{ paddingLeft: '5%', paddingRight: '7%' }}>
            <InputLabel>Start time</InputLabel>
            <TextField
              fullWidth
              id="time"
              type="time"
              name="startTime"
              placeholder="--:-- --"
              format="HH:mm"
              variant="standard"
              value={startTime}
              InputLabelProps={{
                shrink: true,
                min: dayjs().utc().format('HH:mm'),
              }}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={isDynamic}
            />
          </Grid>
        </Grid>
        <Grid item xs={10} sx={{ mt: 1 }}>
          <InputLabel sx={{ pt: '3%', pb: '2%' }}>Deployment Route</InputLabel>
          <MultiSelect
            placeholder="Select Route(s)"
            value={currentRun.routeIds || []}
            options={zoneOptions}
            handleChange={(value) => handleChangeRoute(value)}
            isDisabled={isDynamic}
          />
        </Grid>
        <Grid item xs={10} sx={{ mt: 1, pb: '2%' }}>
          <InputLabel sx={{ pt: '3%', pb: '2%' }}>Warehouse</InputLabel>
          <MultiSelect
            isSingle
            placeholder="Select A Warehouse"
            value={[currentRun.warehouseId] || []}
            options={warehouseOptions}
            handleChange={(value) => handleChangeWarehouse(value)}
            isDisabled={isDynamic}
          />
        </Grid>
        <Grid item container xs={12} md={12} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <InputLabel>Quantity to deploy</InputLabel>
            <TextField
              fullWidth
              margin="dense"
              name="deployQuantity"
              type="number"
              placeholder="--"
              variant="standard"
              value={currentRun.deployQuantity || ''}
              InputProps={{ inputProps: { min: 1 } }}
              style={styles.textField}
              onChange={handleQuantityChange}
              disabled={isDynamic}
            />
          </Grid>
          <Grid item xs={10}>
            <InputLabel>Current Target</InputLabel>
            <TextField
              fullWidth
              margin="dense"
              type="number"
              placeholder="--"
              variant="standard"
              value={currentTarget}
              InputProps={{ inputProps: { min: 0, readOnly: true } }}
              style={styles.textField}
              disabled={isDynamic}
            />
          </Grid>
        </Grid>
        <Grid item xs={10} sx={{ mt: 1, pb: '2%' }}>
          <InputLabel sx={{ pb: '2%' }}>Assign to</InputLabel>
          <MultiSelect
            placeholder="Select Wrangler(s)"
            handleChange={(value) => handleChangeWrangler(value)}
            value={currentRun.wranglerIds || [assignees]}
            options={assigneeOptions}
            isDisabled={currentRun.status === RunStatusNames.EXPIRED}
          />
        </Grid>
        <Grid item xs={10} sx={{ mt: 1, pb: '2%' }}>
          <InputLabel sx={{ pt: '3%', pb: '2%' }}>Status</InputLabel>
          <MultiSelect
            isSingle
            placeholder={currentRun.status || RunStatusNames.TODO}
            handleChange={(value) => handleChangeStatus(value)}
            value={[currentRun.status] || [RunStatusNames.TODO]}
            options={statusOptions}
            isDisabled={
              currentRun.status !== RunStatusNames.IN_PROGRESS
              && currentRun.status !== RunStatusNames.CLOSED
            }
          />
        </Grid>
        <Grid item xs={12}>
          <StartedAndClosedDisplay item={currentRun} tz={tz} />
        </Grid>
      </Grid>
      <RunMap
        vehiclesAndLocations={getVehiclesAndLocations(currentRun)}
        selectedMarket={selectedMarket}
      />
    </Grid>
  );
}

const EditRun = withRouter(BaseEditRunContent);
export { BaseEditRunContent, EditRun };
